import { z } from "zod";
import {
  alibabaBeijingMessages,
  alibabaSingaporeMessages,
  alibabaVirginiaMessages,
} from "../alibaba/v1alpha1/messages";
import { anthropicMessages } from "../anthropic/v1alpha1/messages";
import { openaiResponses } from "../openai/v1alpha1/responses";
import { openrouterChatCompletions } from "../openrouter/v1alpha1/chat-completions";
import { openrouterResponses } from "../openrouter/v1alpha1/responses";
import { vertexGenerateContent } from "../vertex/v1alpha1/generate-content";
import { errorTypeOfStatus, type ModelError, modelError, saysContextTooLong } from "./errors";
import {
  type CallCost,
  type CallFacts,
  priceCall,
  type RoutingFacts,
  type Sku,
  skuById,
} from "./pricing";
import type { ModelReply, ModelTransportDefinition, SentRequest } from "./transport";
import type { ModelUsage } from "./usage";

/**
 * Every vendor endpoint Pax serves model calls on, named for the vendor and the endpoint. A new
 * one is one more file in the vendor's folder and one more entry here: its route, the usage log,
 * and the charge all read this table.
 */
export const modelTransports = {
  "alibaba-beijing-messages": alibabaBeijingMessages,
  "alibaba-singapore-messages": alibabaSingaporeMessages,
  "alibaba-virginia-messages": alibabaVirginiaMessages,
  "anthropic-messages": anthropicMessages,
  "openai-responses": openaiResponses,
  "openrouter-chat-completions": openrouterChatCompletions,
  "openrouter-responses": openrouterResponses,
  "vertex-generate-content": vertexGenerateContent,
} satisfies Record<string, ModelTransportDefinition>;

export type ModelTransport = keyof typeof modelTransports;
export const ModelTransport = z.enum(Object.keys(modelTransports) as ModelTransport[]);

const definition = (transport: ModelTransport): ModelTransportDefinition =>
  modelTransports[transport];

/**
 * Reads the usage a vendor response reports, given the transport it came over and the request
 * that asked for it.
 *
 * The API logs these figures and charges from them, so the log and the charge always describe the
 * same call the same way.
 */
export function readModelUsage(
  transport: ModelTransport,
  responseBody: string,
  requestBody: string,
): ModelUsage | undefined {
  return definition(transport).readUsage(responseBody, requestBody);
}

/** What a call asked for, and, once its response is in, what the response reported. */
export function readCallFacts(
  transport: ModelTransport,
  request: SentRequest,
  responseBody?: string,
): CallFacts {
  const transportDefinition = definition(transport);
  const reply =
    responseBody === undefined ? undefined : transportDefinition.readReply(responseBody);
  const usage =
    responseBody === undefined
      ? undefined
      : transportDefinition.readUsage(responseBody, request.body);
  return {
    billedBy: transportDefinition.billedBy,
    requested: transportDefinition.readRequestedFacts(request),
    served: servedFacts(reply, usage),
  };
}

/** What a response reported about where the call went. A fact it didn't report is left out. */
function servedFacts(reply?: ModelReply, usage?: ModelUsage): Partial<RoutingFacts> {
  const facts: Partial<RoutingFacts> = {
    model: usage?.model ?? reply?.model,
    upstream: reply?.upstreamProvider,
    tier: usage?.serviceTier ?? reply?.serviceTier,
    inferenceLocation: usage?.region ?? reply?.region,
  };
  return Object.fromEntries(
    Object.entries(facts).filter(([, value]) => value !== undefined),
  ) as Partial<RoutingFacts>;
}

/** The SKU a call is billed at, which what it asked for decides. */
export function skuOfCall(
  transport: ModelTransport,
  requested: Partial<RoutingFacts>,
): Sku | undefined {
  return skuById(definition(transport).skuOf(requested));
}

/**
 * A finished call's list cost: its usage, as the response reported it, priced at its SKU. A call
 * whose response reported no usage has one line of unknown usage.
 */
export function priceModelCall(
  transport: ModelTransport,
  facts: CallFacts,
  usage: ModelUsage | undefined,
  receivedAt: Date,
): CallCost {
  return priceCall(skuOfCall(transport, facts.requested), receivedAt, usage, "reported");
}

/** The settings a request asked for: model, token limit, sampling, reasoning, format. */
export function readModelRequestSettings(
  transport: ModelTransport,
  path: string,
  requestBody: string,
) {
  return definition(transport).readSettings(path, requestBody);
}

/** A request body's prompt as ordered parts, and the tool results it carries. */
export function readModelPrompt(transport: ModelTransport, requestBody: string) {
  return definition(transport).readPrompt(requestBody);
}

/** What a response body says the model sent back: reasoning, text, tool calls and how it ended. */
export function readModelReply(transport: ModelTransport, responseBody: string) {
  return definition(transport).readReply(responseBody);
}

/** What each chunk of a streamed response carried, in the order the chunks arrived. */
export function readModelChunks(transport: ModelTransport, responseBody: string) {
  return definition(transport).readChunks(responseBody);
}

/** The tools and response format a request gave the model, to check its reply against. */
export function readModelOutputContract(
  transport: ModelTransport,
  path: string,
  requestBody: string,
) {
  return definition(transport).readOutputContract(path, requestBody);
}

/** How a call ended, as far as whether the vendor answered. */
export interface ModelCallEnding {
  responseStatus?: number;
  /** What was thrown while sending the request or reading its response. */
  failure?: unknown;
  reply: ModelReply;
  streamed: boolean;
}

/**
 * What went wrong with a call that didn't get a whole response, or nothing when it did. An error
 * can arrive inside a stream that started with a 200, and a stream can stop with no error and no
 * finish reason; both count.
 */
export function readModelError(
  transport: ModelTransport,
  ending: ModelCallEnding,
): ModelError | undefined {
  const { responseStatus: status, failure, reply } = ending;
  if (failure !== undefined) {
    const name = failure instanceof Error ? failure.name : "Error";
    const message = failure instanceof Error ? failure.message : String(failure);
    if (name === "AbortError") return modelError("cancelled", name, message);
    if (name === "TimeoutError") return modelError("timeout", name, message);
    return modelError(
      status !== undefined && status < 400 ? "stream_interrupted" : "connection_reset",
      name,
      message,
    );
  }
  const code = reply.errorType;
  const message = reply.errorMessage;
  if (code !== undefined || (status ?? 0) >= 400) {
    const type = saysContextTooLong(message)
      ? "context_length_exceeded"
      : (definition(transport).errorTypeOf(code, message) ?? errorTypeOfStatus(status));
    return modelError(type, code ?? (status === undefined ? undefined : String(status)), message);
  }
  if (ending.streamed && reply.finishReasons.length === 0)
    return modelError("stream_interrupted", undefined, "The stream ended without a finish reason");
}

export {
  AiPayer,
  AiPurpose,
  BENEFICIARIES_HEADER,
  ClientAllowanceSpentData,
  clientAllowanceSpent,
  GameCallHeaders,
  GameCallPurpose,
} from "./calls";
export {
  ERROR_RULES_VERSION,
  type ModelError,
  type ModelErrorType,
  modelError,
} from "./errors";
export { isRecord, jsonObjects, readableText } from "./json-body";
export { canonicalModelName, modelCreator } from "./models";
export {
  type CallCost,
  type CallFacts,
  CostLine,
  type CostPrediction,
  isReportedCostSku,
  Meter,
  priceCall,
  priceCeiling,
  ReservedFrom,
  type RoutingFacts,
  type Sku,
  skus,
} from "./pricing";
export { ENCRYPTED_REASONING_LABEL, sharedPromptPrefix } from "./prompt";
export type {
  ModelReply,
  ModelRequestSettings,
  ModelTransportDefinition,
  OutputContract,
  PromptPart,
  SentRequest,
  StreamChunk,
  TransportRefusal,
} from "./transport";
export { ModelUsage } from "./usage";
export { type PaxRefusal, paxRefusalStatus, vendorErrorResponse } from "./vendor-errors";
export { AiVendor, aiVendorBasePath, aiVendors } from "./vendors";
